/**
 * Sélecteur « Vue : <profil> » et indicateur « Rôle d'accès » (planche `Nav`, B-602).
 *
 * Deux informations, jamais confondues : la VUE (un profil exercé, choisie en un geste,
 * persistée par utilisateur et par espace) et le RÔLE D'ACCÈS (les droits, lus du serveur,
 * non modifiables ici). Une vue non encore choisie est dite « suggérée ».
 */
import type { JSX } from 'react'

import { SelectNatif } from '@/components/ui/native-select'
import { useProfil } from '@/hooks/useProfil'
import { useT } from '@/i18n/language'
import { LIBELLES_ROLE, LIBELLES_VUE, estVue, vuesProposees } from '@/lib/profils'
import { canAccessAdministration } from '@/auth/session'
import { useSession } from '@/hooks/useSession'

export function SelecteurVue(): JSX.Element {
  const { t } = useT()
  const { vue, choisie, role, setVue, erreur, enregistrement, revenirALaSuggestion } = useProfil()
  // Lot NAV 0.5.x : « Administration » n'est proposée qu'à qui peut administrer.
  const session = useSession()
  const vues = vuesProposees(canAccessAdministration(session))
  const roles = session?.roles ?? []
  const roleOrganisation = roles.includes('org_admin')
    ? t('role.orgAdmin', 'Administratrice/Administrateur d’organisation')
    : roles.includes('auditor') ? t('role.auditor', 'Auditrice/Auditeur') : null
  return (
    <div className="space-y-1 px-2 py-1 text-xs" data-testid="selecteur-vue">
      <div className="flex items-center gap-2">
        <label htmlFor="vue-profil" className="shrink-0 text-muted-foreground">{t('profil.vueLabel', 'Vue')}</label>
        <SelectNatif id="vue-profil" data-testid="vue-profil" value={vue} aria-busy={enregistrement}
                     className="h-10 min-w-0 flex-1 text-sm"
                     onChange={(e) => { if (estVue(e.target.value)) setVue(e.target.value) }}>
          {vues.map((v) => <option key={v} value={v}>{t(`profil.${v}`, LIBELLES_VUE[v])}</option>)}
        </SelectNatif>
      </div>
      {!choisie ? (
        <p className="text-muted-foreground" data-testid="vue-suggeree">
          {t('profil.suggeree', 'Vue suggérée d’après votre rôle : choisissez la vôtre.')}
        </p>
      ) : (
        <button type="button" onClick={revenirALaSuggestion} data-testid="vue-reinitialiser"
                className="inline-flex min-h-10 items-center text-left text-muted-foreground underline underline-offset-4 hover:text-foreground">
          {t('profil.revenirSuggestion', 'Revenir à la vue suggérée')}
        </button>
      )}
      <p className="text-muted-foreground" data-testid="role-acces">
        {t('profil.roleAcces', 'Rôle d’accès : {{role}}', { role: t(`profil.role.${role}`, LIBELLES_ROLE[role]) })}
      </p>
      {/* v05x-org-neuve : le rôle d'accès est celui du PROJET ouvert ; une administratrice d'organisation
          lisait « propriétaire » sans voir son rôle d'organisation (parcours réel du 2026-10-05). */}
      {roleOrganisation ? (
        <p className="text-muted-foreground" data-testid="role-organisation">
          {t('profil.roleOrganisation', 'Rôle dans l’organisation : {{role}}', { role: roleOrganisation })}
        </p>
      ) : null}
      {erreur ? <p role="alert" className="text-[var(--color-error-text)]">{erreur}</p> : null}
    </div>
  )
}
